import React, { useState } from 'react';
import { ConfirmationModal } from '../../components/shared';
import Loading from '../../components/Loading/Loading';
import PullToRefresh from '../../components/PullToRefresh/PullToRefresh';
import Notification from '../../components/Notification/Notification';

// Import custom hooks
import { useGameState, usePreloadLeaderboardData } from './hooks';

// Import components
import GameIntro from './components/GameIntro/GameIntro';
import GameHeader from './components/GameHeader/GameHeader';
import PhaseNavigation from './components/PhaseNavigation/PhaseNavigation';
import Phase0 from './components/Phase0/Phase0';
import Phase1 from './components/Phase1/Phase1';
import Phase2 from './components/Phase2/Phase2';
import Phase3 from './components/Phase3/Phase3';
import Leaderboard from './components/Leaderboard/Leaderboard';

import './ChampionsLeagueGame.css';

const ChampionsLeagueGame = () => {
  const gameState = useGameState();

  // Preload leaderboard data (henter alle spilleres data til Redux)
  const { refetchAll: refetchLeaderboardData } = usePreloadLeaderboardData();
  // Confirmation modal state
  const [saveConfirmModal, setSaveConfirmModal] = useState({ isOpen: false, group: '' });
  
  // Vis loading spinner mens data hentes fra database
  if (gameState.settingsLoading || gameState.gameDataLoading || gameState.teamsLoading) {
    return (
      <div className="champions-league-game">
        <div className="champions-league-background"></div>
        <GameHeader />
        <div className="loading-container">
          <Loading size="large" text="" />
        </div>
      </div>
    );
  }

  // Calculate which phases are available based on game progress
  const getAvailablePhases = () => {
    const available = [0]; // Phase 0 is always available
    
    // Phase 1 is available if Phase 0 is completed AND saved
    if (gameState.savedGuesses.phase0) {
      available.push(1);
    }
    
    // Phase 2 is available if Phase 1 is completed AND saved
    if (gameState.savedGuesses.phase1) {
      available.push(2);
    }
    
    // Phase 3 is available if Phase 2 is completed AND saved
    if (gameState.savedGuesses.phase2) {
      available.push(3);
    }
    
    return available;
  };

  const availablePhases = getAvailablePhases();

  const handleSaveGuess = () => {
    setSaveConfirmModal({ isOpen: true, group: '' });
  };

  const handleSaveGroupGuess = (group) => {
    setSaveConfirmModal({ isOpen: true, group });
  };
 
  const confirmSaveGuess = async () => {
    const group = saveConfirmModal.group;
    
    // Gem kun gættet uden at beregne point
    const dataToSave = {
      preseasonWinner: gameState.preseasonWinner,
      leagueStanding: gameState.leagueStanding,
      playoffPredictions: gameState.playoffPredictions,
      knockoutPredictions: gameState.knockoutPredictions,
      finalPrediction: gameState.finalPrediction,
      // VIGTIGT: Send ikke points/phasePoints herfra.
      // Point beregnes og gemmes af backend når realResults opdateres.
    };

    // Hvis det er en phase 2 gruppe, marker hvilken gruppe der gemmes
    if (group) {
      dataToSave.phase2Group = group;
    }
    
    await gameState.saveToDatabase(dataToSave);
    
    // Vis bekræftelse
    const groupName = group === 'Playoff' ? 'Playoff' : 
                     group === 'Round16' ? 'Round of 16' :
                     group === 'Quarters' ? 'Quarterfinals' :
                     group === 'Semis' ? 'Semifinals' : 
                     gameState.currentPhase === 3 ? 'Finale' : '';
    Notification({ open: true, message: groupName ? `${groupName} gæt gemt!` : 'Gæt gemt!', type: 'success' });
    setSaveConfirmModal({ isOpen: false, group: '' });
  };
 
  const handleLeaderboardClick = () => {
    // Store current phase before going to leaderboard
    gameState.setCurrentPhase(4);
  };
 
  const handlePlayoffPredictionChange = (matchId, value) => {
    const newPredictions = { ...gameState.playoffPredictions };
    newPredictions[matchId] = value;
    gameState.setPlayoffPredictions(newPredictions);
  };
  
  const handleKnockoutPredictionChange = (round, matchId, value) => {
    const newPredictions = { ...gameState.knockoutPredictions };
    if (!newPredictions[round]) {
      newPredictions[round] = {};
    }
    newPredictions[round][matchId] = value;
    gameState.setKnockoutPredictions(newPredictions);
  };
  
  const renderCurrentPhase = () => {
    // Check if we're showing leaderboard
    if (gameState.currentPhase === 4) {
      return <Leaderboard onRefresh={refetchLeaderboardData} />;
    }
    
    // Render normal phases
    switch (gameState.currentPhase) {
      case 0:
        return (
        <Phase0
          preseasonWinner={gameState.preseasonWinner}
          onPreseasonWinnerChange={gameState.setPreseasonWinner}
          onSaveToDatabase={gameState.saveToDatabase}
          isSaved={gameState.savedGuesses.phase0}
          onSaveGuess={handleSaveGuess}
          teams={gameState.teams}
        />
        );
      case 1:
        return (
          <Phase1
            leagueStanding={gameState.leagueStanding}
            onLeagueStandingChange={(index, team) => {
              const newStanding = [...gameState.leagueStanding];
              newStanding[index] = team;
              gameState.setLeagueStanding(newStanding);
            }}
            onSaveToDatabase={gameState.saveToDatabase}
            isSaved={gameState.savedGuesses.phase1}
            onSaveGuess={handleSaveGuess}
            teams={gameState.teams}
          />
        );
      case 2:
        return (
          <Phase2
            playoffPredictions={gameState.playoffPredictions}
            knockoutPredictions={gameState.knockoutPredictions}
            savedPlayoffPredictions={gameState.savedPlayoffPredictions}
            savedKnockoutPredictions={gameState.savedKnockoutPredictions}
            onPlayoffPredictionChange={handlePlayoffPredictionChange}
            onKnockoutPredictionChange={handleKnockoutPredictionChange}
            phaseLocks={gameState.phaseLocks}
            realResults={gameState.realResults}
            savedGuesses={gameState.savedGuesses}
            onSaveGroupGuess={handleSaveGroupGuess}
          />
        );
      case 3:
        return (
          <Phase3
            finalPrediction={gameState.finalPrediction}
            onFinalPredictionChange={gameState.setFinalPrediction}
            realResults={gameState.realResults}
            phaseLocks={gameState.phaseLocks}
            savedGuesses={gameState.savedGuesses}
            onSaveGroupGuess={handleSaveGroupGuess}
            preseasonWinner={gameState.preseasonWinner}
          />
        );
      default:
        return <></>;
    }
  };
  
  if (!gameState.gameStarted) {
    return <GameIntro onStartGame={gameState.startGame} />;
  }

  const refreshPage = async () => {
    const jobs = [gameState.refreshGame()];
    if (gameState.currentPhase === 4) jobs.push(refetchLeaderboardData());
    await Promise.all(jobs);
  };

  return (
    <PullToRefresh onRefresh={refreshPage}>
    <div className="champions-league-game">
      <div className="champions-league-background"></div>
      
      <GameHeader
        points={gameState.points}
        onLeaderboardClick={handleLeaderboardClick}
      />
      
      <PhaseNavigation
        currentPhase={gameState.currentPhase}
        onPhaseChange={gameState.setCurrentPhase}
        availablePhases={availablePhases}
        phaseLocks={gameState.phaseLocks}
      />

      <div className="phase-content">
        {renderCurrentPhase()}
      </div>

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={saveConfirmModal.isOpen}
        onClose={() => setSaveConfirmModal({ isOpen: false, group: '' })}
        onConfirm={confirmSaveGuess}
        title="Gem gæt"
        message={saveConfirmModal.group 
          ? `Er du sikker på at du vil gemme dit ${saveConfirmModal.group === 'Playoff' ? 'Playoff' : saveConfirmModal.group === 'Round16' ? 'Round of 16' : saveConfirmModal.group === 'Quarters' ? 'Quarterfinals' : saveConfirmModal.group === 'Semis' ? 'Semifinals' : ''} gæt?\n\n Når du gemmer dit gæt, kan det ikke ændres senere!`
          : gameState.currentPhase === 0 
            ? `Er du sikker på at ${gameState.preseasonWinner} vil vinde Champions League?`
            : gameState.currentPhase === 3
            ? `Er du sikker på at du vil gemme dit finale gæt?\n\n Når du gemmer dit gæt, kan det ikke ændres senere!`
            : `Er du sikker på at du vil gemmme dit gæt?\n\n Når du gemmer dit gæt, kan det ikke ændres senere!`
        }
        confirmText="Gem"
        cancelText="Annuller"
        confirmButtonType="confirmation"
      />

      <ConfirmationModal
        isOpen={gameState.resetConfirmModal.isOpen}
        onClose={() => gameState.setResetConfirmModal({ isOpen: false })}
        onConfirm={gameState.confirmResetGame}
        title="Nulstil spil"
        message="Er du sikker på at du vil nulstille spillet? Alle dine gæt vil blive slettet."
        confirmText="Nulstil"
        cancelText="Annuller"
        confirmButtonType="danger"
      />
    </div>
    </PullToRefresh>
  );
};

export default ChampionsLeagueGame;
